import { useEffect, useState, useMemo } from "react";
import { Plus, Filter, Loader2, X, ArrowUpDown, ChevronDown, Share2, Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { listAnime, STATUS_LABELS, checkBadges } from "../lib/api";
import { useAuth } from "../lib/auth";
import AnimeCard from "../components/AnimeCard";
import SearchDialog from "../components/SearchDialog";
import AiSearchBar from "../components/AiSearchBar";
import ReactivateBanner from "../components/ReactivateBanner";
import TrialEndingBanner from "../components/TrialEndingBanner";
import BadgeCase from "../components/BadgeCase";

// ----- Sort options ---------------------------------------------------------
const SORT_OPTIONS = [
  { key: "az", label: "A → Z" },
  { key: "rating_desc", label: "Highest Rated" },
  { key: "year_desc", label: "Newest Release" },
  { key: "year_asc", label: "Oldest Release" },
];

const DEFAULT_FILTERS = {
  status: "",
  genre: "",
  type: "all", // all | tv | movie
  yearMin: "",
  yearMax: "",
  ratingMin: "",
  ratingMax: "",
  unratedOnly: false,
};

// Extract a year from an entry, prefering explicit `year`, falling back to aired_from.
function entryYear(e) {
  if (typeof e.year === "number") return e.year;
  const from = e.aired_from;
  if (typeof from === "string" && from.length >= 4) {
    const y = parseInt(from.slice(0, 4), 10);
    if (!Number.isNaN(y)) return y;
  }
  return null;
}

function matchesType(entry, type) {
  if (type === "all") return true;
  const t = (entry.type || "").toLowerCase();
  if (type === "movie") return t === "movie";
  if (type === "tv") return t === "tv" || t === "tv special";
  return true;
}

function applyFilters(entries, f) {
  return entries.filter((e) => {
    if (f.status && e.status !== f.status) return false;
    if (f.genre && !(e.genres || []).includes(f.genre)) return false;
    if (!matchesType(e, f.type)) return false;

    const year = entryYear(e);
    if (f.yearMin && (year == null || year < Number(f.yearMin))) return false;
    if (f.yearMax && (year == null || year > Number(f.yearMax))) return false;

    const r = e.user_rating;
    if (f.unratedOnly) {
      if (r != null) return false;
    } else {
      if (f.ratingMin !== "" && (r == null || r < Number(f.ratingMin))) return false;
      if (f.ratingMax !== "" && (r == null || r > Number(f.ratingMax))) return false;
    }
    return true;
  });
}

function applySort(entries, key) {
  const arr = [...entries];
  if (key === "az") {
    arr.sort((a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" }));
  } else if (key === "rating_desc") {
    // Unrated items sink to the bottom so top slots are always meaningful.
    arr.sort((a, b) => {
      const ra = a.user_rating == null ? -Infinity : a.user_rating;
      const rb = b.user_rating == null ? -Infinity : b.user_rating;
      if (rb !== ra) return rb - ra;
      return (a.title || "").localeCompare(b.title || "");
    });
  } else if (key === "year_desc") {
    arr.sort((a, b) => {
      const ya = entryYear(a) ?? -Infinity;
      const yb = entryYear(b) ?? -Infinity;
      if (yb !== ya) return yb - ya;
      return (a.title || "").localeCompare(b.title || "");
    });
  } else if (key === "year_asc") {
    arr.sort((a, b) => {
      const ya = entryYear(a) ?? Infinity;
      const yb = entryYear(b) ?? Infinity;
      if (ya !== yb) return ya - yb;
      return (a.title || "").localeCompare(b.title || "");
    });
  }
  return arr;
}

// Build a list of "active filter" chips for the sticky bar.
function buildActiveChips(f) {
  const chips = [];
  if (f.status) chips.push({ id: "status", label: STATUS_LABELS[f.status] || f.status, patch: { status: "" } });
  if (f.genre) chips.push({ id: "genre", label: f.genre, patch: { genre: "" } });
  if (f.type !== "all") chips.push({ id: "type", label: f.type === "movie" ? "Movies" : "TV Series", patch: { type: "all" } });
  if (f.yearMin || f.yearMax) {
    chips.push({
      id: "year",
      label: `Year ${f.yearMin || "…"}–${f.yearMax || "…"}`,
      patch: { yearMin: "", yearMax: "" },
    });
  }
  if (f.unratedOnly) {
    chips.push({ id: "unrated", label: "Unrated only", patch: { unratedOnly: false } });
  } else if (f.ratingMin !== "" || f.ratingMax !== "") {
    chips.push({
      id: "rating",
      label: `Rating ${f.ratingMin === "" ? "…" : f.ratingMin}–${f.ratingMax === "" ? "…" : f.ratingMax}`,
      patch: { ratingMin: "", ratingMax: "" },
    });
  }
  return chips;
}

// ----------------------------------------------------------------------------

export default function Catalogue() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState("az");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [badgeState, setBadgeState] = useState({ catalog: [], earned: [] });

  // Fire the check-and-award endpoint after every catalogue change, then
  // toast each newly-earned badge so users get instant feedback.
  const runBadgeCheck = async () => {
    try {
      const res = await checkBadges();
      setBadgeState({ catalog: res.catalog || [], earned: res.earned || [] });
      const catalogMap = new Map((res.catalog || []).map((b) => [b.key, b]));
      for (const key of res.newly_earned || []) {
        const b = catalogMap.get(key);
        toast.success(`Badge unlocked — ${b?.name || key}`, {
          description: b?.description || "",
        });
      }
    } catch (err) {
      // Non-fatal: badge check is a nice-to-have, not required for add-flow.
      console.warn("Badge check failed:", err);
    }
  };

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const clearAll = () => {
    setFilters(DEFAULT_FILTERS);
    setSortKey("az");
  };

  // Load the whole catalogue once — every filter (including status & genre)
  // is applied client-side so the Genre dropdown always lists every genre the
  // user has ever added, not just the ones in the currently-filtered subset.
  const load = async () => {
    setLoading(true);
    try {
      const data = await listAnime();
      setEntries(data.entries || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); runBadgeCheck(); }, []);

  const allGenres = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => (e.genres || []).forEach((g) => s.add(g)));
    return [...s].sort();
  }, [entries]);

  const filtered = useMemo(() => applyFilters(entries, filters), [entries, filters]);
  const sorted = useMemo(() => applySort(filtered, sortKey), [filtered, sortKey]);
  const chips = useMemo(() => buildActiveChips(filters), [filters]);
  const hasActive = chips.length > 0 || sortKey !== "az";

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-10 md:px-10">
      <ReactivateBanner />
      <TrialEndingBanner />
      <WrappedBanner entryCount={entries.length} />
      {/* Hero */}
      <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 01</p>
          <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
            Your<br/>Catalogue
          </h1>
          <p className="mt-4 max-w-lg text-sm text-neutral-400">
            Every anime you&apos;ve touched, indexed and analyzed. Pulled straight from MyAnimeList via the Jikan API — no logins, no tracking, just receipts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-end">
          <CopyProfileLinkButton user={user} />
          <button
            data-testid="open-search-dialog"
            onClick={() => setDialogOpen(true)}
            className="group flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-white hover:text-black"
          >
            <Plus className="h-4 w-4" />
            Add Anime
          </button>
        </div>
      </div>

      {/* AI Search */}
      <AiSearchBar onResults={(r) => setAiResults(r)} onClear={() => setAiResults(null)} />

      {/* Filter toolbar */}
      <FilterToolbar
        filters={filters}
        sortKey={sortKey}
        setSortKey={setSortKey}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        chips={chips}
        hasActive={hasActive}
        onClear={clearAll}
        onChipRemove={(chip) => patchFilters(chip.patch)}
        entryCount={sorted.length}
        totalCount={entries.length}
        aiActive={aiResults != null}
      />

      {/* Collapsible filter panel */}
      <FilterPanel
        open={filtersOpen}
        filters={filters}
        onChange={patchFilters}
        allGenres={allGenres}
      />

      {/* Grid */}
      {(() => {
        if (loading) {
          return (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
            </div>
          );
        }
        const shownEntries = aiResults != null ? aiResults : sorted;
        if (shownEntries.length === 0) {
          if (aiResults != null) {
            return (
              <p data-testid="ai-no-results" className="py-16 text-center font-mono text-xs uppercase tracking-widest text-neutral-500">
                No AI matches — try clearing the search or rephrasing.
              </p>
            );
          }
          if (entries.length === 0) {
            return <EmptyState onAdd={() => setDialogOpen(true)} />;
          }
          return <NoMatchState onClear={clearAll} />;
        }
        return (
          <div data-testid="anime-grid" className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {shownEntries.map((e, i) => (
              <AnimeCard key={e.mal_id} entry={e} index={i} />
            ))}
          </div>
        );
      })()}

      <SearchDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onAdded={() => { load(); runBadgeCheck(); }} />

      {/* Badge case — free-tier achievement grid */}
      <BadgeCase catalog={badgeState.catalog} earned={badgeState.earned} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Year in Review banner — surfaces in December + January so users see their
// Wrapped season. Any other month you can still hit /wrapped/{year} directly.
// ---------------------------------------------------------------------------
function WrappedBanner({ entryCount }) {
  const now = new Date();
  const month = now.getMonth(); // 0 = Jan, 11 = Dec
  const isSeason = month === 11 || month === 0;
  if (!isSeason || !entryCount) return null;
  const wrappedYear = month === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return (
    <Link
      to={`/wrapped/${wrappedYear}`}
      data-testid="wrapped-banner"
      className="mb-8 flex items-center justify-between gap-4 border border-[#FF3B30]/50 bg-gradient-to-r from-[#FF3B30]/20 via-[#FF3B30]/10 to-transparent p-5 transition-all hover:from-[#FF3B30]/30"
    >
      <div className="flex items-start gap-4">
        <div className="mt-1 flex h-10 w-10 items-center justify-center border border-[#FF3B30] bg-black">
          <Sparkles className="h-5 w-5 text-[#FF3B30]" />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF3B30]">// Year in Review</p>
          <h3 className="mt-1 font-display text-2xl uppercase leading-none tracking-tight text-white md:text-3xl">
            Your {wrappedYear} Wrapped is ready
          </h3>
          <p className="mt-1 text-sm text-neutral-300">
            A quick recap of your anime year — swipe through and share.
          </p>
        </div>
      </div>
      <span
        aria-hidden="true"
        className="hidden shrink-0 items-center gap-2 border border-white/20 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-black md:flex"
      >
        Open <ArrowUpDown className="h-3 w-3 rotate-90" />
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Copy profile link — only visible when the signed-in owner has flipped
// their profile public in Account settings. Shows nothing otherwise.
// ---------------------------------------------------------------------------
function CopyProfileLinkButton({ user }) {
  const [copied, setCopied] = useState(false);
  if (!user || !user.profile_public || !user.handle) return null;
  const url = `${window.location.origin}/u/${user.handle}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Profile link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — copy manually");
    }
  };
  return (
    <button
      data-testid="copy-profile-link-btn"
      onClick={copy}
      title={url}
      className="flex items-center gap-2 border border-white/20 bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:border-[#FF3B30]"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Share2 className="h-4 w-4" />}
      {copied ? "Copied" : "Copy profile link"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filter toolbar (always visible)
// ---------------------------------------------------------------------------
function FilterToolbar({
  filters, sortKey, setSortKey, filtersOpen, setFiltersOpen,
  chips, hasActive, onClear, onChipRemove, entryCount, totalCount, aiActive,
}) {
  const activeCount = chips.length;
  return (
    <div className="mb-4 border-b border-white/10 pb-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="toggle-filters"
          onClick={() => setFiltersOpen((v) => !v)}
          className={`flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
            filtersOpen
              ? "border-[#FF3B30] bg-[#FF3B30] text-white"
              : "border-white/15 bg-transparent text-white hover:border-[#FF3B30]"
          }`}
        >
          <Filter className="h-3 w-3" />
          Filters
          {activeCount > 0 && (
            <span
              data-testid="active-filter-count"
              className="ml-1 rounded-full bg-white/15 px-1.5 py-0.5 text-[9px] leading-none text-white"
            >
              {activeCount}
            </span>
          )}
          <ChevronDown className={`h-3 w-3 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
        </button>

        <div className="relative flex items-center gap-2">
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            <ArrowUpDown className="h-3 w-3" />
            Sort
          </span>
          <select
            data-testid="sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="border border-white/15 bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white focus:outline-none focus:border-[#FF3B30]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        {hasActive && (
          <button
            data-testid="clear-all-filters"
            onClick={onClear}
            className="flex items-center gap-1.5 border border-white/15 bg-transparent px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-300 hover:border-[#FF3B30] hover:text-white"
          >
            <X className="h-3 w-3" />
            Clear All
          </button>
        )}

        <span
          data-testid="entry-count"
          className="ml-auto font-mono text-[11px] uppercase tracking-widest text-neutral-500"
        >
          {aiActive ? "AI results" : `${entryCount} of ${totalCount} ${totalCount === 1 ? "entry" : "entries"}`}
        </span>
      </div>

      {/* Active chip strip */}
      {chips.length > 0 && (
        <div data-testid="active-filter-chips" className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.id}
              data-testid={`chip-${chip.id}`}
              onClick={() => onChipRemove(chip)}
              className="group flex items-center gap-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-[#FF3B30]/25"
            >
              {chip.label}
              <X className="h-3 w-3 opacity-70 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter panel (expanded on demand)
// ---------------------------------------------------------------------------
function FilterPanel({ open, filters, onChange, allGenres }) {
  if (!open) return null;
  return (
    <div
      data-testid="filter-panel"
      className="mb-6 grid grid-cols-1 gap-6 border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <FilterGroup label="Status">
        <select
          data-testid="status-filter"
          value={filters.status}
          onChange={(e) => onChange({ status: e.target.value })}
          className={selectCls}
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup label="Genre">
        <select
          data-testid="genre-filter"
          value={filters.genre}
          onChange={(e) => onChange({ genre: e.target.value })}
          className={selectCls}
        >
          <option value="">All genres</option>
          {allGenres.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup label="Type">
        <div role="tablist" className="flex overflow-hidden border border-white/15">
          {[
            { key: "all", label: "All" },
            { key: "tv", label: "TV Series" },
            { key: "movie", label: "Movies" },
          ].map((opt) => (
            <button
              key={opt.key}
              data-testid={`type-${opt.key}`}
              onClick={() => onChange({ type: opt.key })}
              className={`flex-1 px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                filters.type === opt.key
                  ? "bg-[#FF3B30] text-white"
                  : "bg-transparent text-neutral-400 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="Release Year">
        <div className="flex items-center gap-2">
          <input
            data-testid="year-min"
            type="number"
            inputMode="numeric"
            placeholder="From"
            value={filters.yearMin}
            min={1900}
            max={2100}
            onChange={(e) => onChange({ yearMin: e.target.value })}
            className={numberCls}
          />
          <span className="text-neutral-600">–</span>
          <input
            data-testid="year-max"
            type="number"
            inputMode="numeric"
            placeholder="To"
            value={filters.yearMax}
            min={1900}
            max={2100}
            onChange={(e) => onChange({ yearMax: e.target.value })}
            className={numberCls}
          />
        </div>
      </FilterGroup>

      <FilterGroup label="Your Rating" className="sm:col-span-2 lg:col-span-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="rating-min"
            type="number"
            inputMode="decimal"
            step="0.5"
            min={0}
            max={10}
            placeholder="Min"
            value={filters.ratingMin}
            disabled={filters.unratedOnly}
            onChange={(e) => onChange({ ratingMin: e.target.value })}
            className={`${numberCls} disabled:opacity-40`}
          />
          <span className="text-neutral-600">–</span>
          <input
            data-testid="rating-max"
            type="number"
            inputMode="decimal"
            step="0.5"
            min={0}
            max={10}
            placeholder="Max"
            value={filters.ratingMax}
            disabled={filters.unratedOnly}
            onChange={(e) => onChange({ ratingMax: e.target.value })}
            className={`${numberCls} disabled:opacity-40`}
          />
          <label
            data-testid="unrated-only"
            className={`flex cursor-pointer items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              filters.unratedOnly
                ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                : "border-white/15 text-neutral-400 hover:text-white"
            }`}
          >
            <input
              type="checkbox"
              className="hidden"
              checked={filters.unratedOnly}
              onChange={(e) => onChange({ unratedOnly: e.target.checked })}
            />
            Unrated only
          </label>
        </div>
      </FilterGroup>
    </div>
  );
}

function FilterGroup({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</p>
      {children}
    </div>
  );
}

const selectCls =
  "w-full border border-white/15 bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white focus:outline-none focus:border-[#FF3B30]";
const numberCls =
  "w-24 border border-white/15 bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF3B30]";

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------
function EmptyState({ onAdd }) {
  return (
    <div data-testid="empty-catalogue" className="border border-dashed border-white/15 py-20 text-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-neutral-600">// Empty</p>
      <h2 className="mt-4 font-display text-5xl uppercase text-white">No anime yet</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
        Start with something you loved. We&apos;ll pull episodes, runtime, and genres from MyAnimeList automatically.
      </p>
      <button
        data-testid="empty-add-btn"
        onClick={onAdd}
        className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        <Plus className="h-4 w-4" /> Add your first
      </button>
    </div>
  );
}

function NoMatchState({ onClear }) {
  return (
    <div data-testid="no-match-state" className="border border-dashed border-white/15 py-20 text-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-neutral-600">// No matches</p>
      <h2 className="mt-4 font-display text-5xl uppercase text-white">Nothing fits<br/>those filters</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
        Try loosening the year or rating range, switching types, or removing a chip — your catalogue is still here, just hidden.
      </p>
      <button
        data-testid="no-match-clear-btn"
        onClick={onClear}
        className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-transparent px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        <X className="h-4 w-4" /> Clear all filters
      </button>
    </div>
  );
}
